// httpPorts — the Quality BC slice: the two material-level evaluations (the raw
// signal axes + the policy roll-up), both reconstructed client-side from the
// one pure `GET /quality` read.

import * as Core from "@bilda/core";
import { Result } from "@bilda/utils";
import type {
    Grounding,
    MaterialQuality,
    Readability,
    ReadabilityBand,
    SectionLix,
    StatementBacking,
} from "../../components/QualitySummary/QualitySummary.js";
import { type ExternalError, mkMalformed } from "../externalError.js";
import { getQuality, readEnvelope } from "../httpClient.js";
import type { MaterialQualityRead, QualityPorts } from "../ports/quality.js";
import type { PortResult } from "../ports/result.js";
import type { MaterialId } from "../ports.js";
import { fields, list, num, str } from "../wireShape.js";
import { parseClaim } from "./claims.js";

// Both quality axes are PURE server computations (no LLM, no key). Linguistics
// (LIX) is expected to succeed for any loaded material, so a failure there is a
// genuine error the host surfaces. Grounding refuses with `no_sources` when the
// material has no source edges yet — read as the advisory "no-sources" state,
// not a hard failure. SHORTCUT: any grounding refusal degrades to no-sources; a
// loaded material won't hit the missing/wrong-kind branches. See docs/analysis.

export const isBand = (s: string | undefined): s is ReadabilityBand =>
    s === "very_easy" ||
    s === "easy" ||
    s === "medium" ||
    s === "hard" ||
    s === "very_hard";

const isFactualKind = (
    s: string | undefined,
): s is Core.Quality.CitationCoverage.FactualKind =>
    s === "question" || s === "passage";

// Claim backing per factual kind. A kind or item we can't read is dropped
// (forward-compat), never guessed.
const readSupport = (
    v: unknown,
): Core.Quality.CitationCoverage.CitationCoverage =>
    list(v).flatMap((k): Core.Quality.CitationCoverage.KindCoverage[] => {
        const kf = fields(k);
        const kind = str(kf?.get("kind"));
        if (!isFactualKind(kind)) return [];
        const uncited = list(kf?.get("uncited")).flatMap(
            (u): Core.Quality.CitationCoverage.FactualItem[] => {
                const uf = fields(u);
                const id = str(uf?.get("id"));
                if (id === undefined) return [];
                return [
                    {
                        kind,
                        id: Core.Resource.Ast.mkNodeId(id),
                        text: str(uf?.get("text")) ?? "",
                    },
                ];
            },
        );
        return [
            {
                kind,
                total: num(kf?.get("total")) ?? 0,
                cited: num(kf?.get("cited")) ?? 0,
                uncited,
            },
        ];
    });

const readSections = (v: unknown): readonly SectionLix[] =>
    list(v).flatMap((sec): SectionLix[] => {
        const f = fields(sec);
        const id = str(f?.get("id"));
        const lix = fields(f?.get("lix"));
        if (id === undefined) return [];
        const cat = str(lix?.get("category"));
        return [
            {
                id,
                title: str(f?.get("title")) ?? "",
                lix: num(lix?.get("score")) ?? 0,
                band: isBand(cat) ? cat : undefined,
            },
        ];
    });

const readGrounding = (r: Result.Result<unknown, ExternalError>): Grounding => {
    if (Result.isErr(r)) return { type: "no-sources" };
    const f = fields(r.value);
    const sourceTitles = list(f?.get("sourcesConsulted"))
        .map((s) => str(fields(s)?.get("title")))
        .filter((title): title is string => title !== undefined);
    return {
        type: "grounded",
        support: readSupport(f?.get("support")),
        backing: list(f?.get("backing")).flatMap(
            (b): readonly StatementBacking[] => {
                const bf = fields(b);
                const node = str(bf?.get("node"));
                const claims = list(bf?.get("claims")).flatMap(parseClaim);
                return node === undefined || claims.length === 0
                    ? []
                    : [{ node, text: str(bf?.get("text")) ?? "", claims }];
            },
        ),
        sourceTitles,
    };
};

// One `GET /quality` read answers both axes for every requested material, as
// the same envelopes the two commands answer. A material absent from the answer
// reads as malformed (never guessed).
type AxisReads = {
    readonly linguistics: Result.Result<unknown, ExternalError>;
    readonly grounding: Result.Result<unknown, ExternalError>;
};

const omitted = Result.err(mkMalformed("quality read omitted the material"));

const readAxes = async (
    ids: readonly MaterialId[],
): PortResult<ReadonlyMap<string, AxisReads>> => {
    const r = await getQuality(ids);
    if (Result.isErr(r)) return r;
    return Result.ok(
        new Map(
            list(fields(r.value)?.get("materials")).flatMap(
                (m): [string, AxisReads][] => {
                    const f = fields(m);
                    const id = str(f?.get("id"));
                    if (id === undefined) return [];
                    return [
                        [
                            id,
                            {
                                linguistics: readEnvelope(
                                    f?.get("linguistics"),
                                ),
                                grounding: readEnvelope(f?.get("grounding")),
                            },
                        ],
                    ];
                },
            ),
        ),
    );
};

const axesOf = async (id: MaterialId): PortResult<AxisReads> => {
    const r = await readAxes([id]);
    if (Result.isErr(r)) return r;
    const axes = r.value.get(id);
    return axes === undefined ? omitted : Result.ok(axes);
};

const qualityOf = (
    axes: AxisReads,
): Result.Result<MaterialQuality, ExternalError> => {
    if (Result.isErr(axes.linguistics)) return axes.linguistics;
    const ling = fields(axes.linguistics.value);
    const lix = fields(ling?.get("lix"));
    const catStr = str(lix?.get("category"));
    const readability: Readability = {
        lix: num(lix?.get("score")) ?? 0,
        band: isBand(catStr) ? catStr : undefined,
        words: num(lix?.get("words")) ?? 0,
        sentences: num(lix?.get("sentences")) ?? 0,
        sections: readSections(ling?.get("sections")),
    };
    return Result.ok({ grounding: readGrounding(axes.grounding), readability });
};

const evaluateMaterial = async (
    materialId: MaterialId,
): PortResult<MaterialQuality> => {
    const axes = await axesOf(materialId);
    return Result.isErr(axes) ? axes : qualityOf(axes.value);
};

// Every material in ONE request (a chat's panel reads all its materials at
// once); each material keeps its own result.
const evaluateMaterials = async (
    ids: readonly MaterialId[],
): PortResult<readonly MaterialQualityRead[]> => {
    const r = await readAxes(ids);
    if (Result.isErr(r)) return r;
    return Result.ok(
        ids.map((id) => {
            const axes = r.value.get(id);
            return { id, read: axes === undefined ? omitted : qualityOf(axes) };
        }),
    );
};

// The policy report card. The App `evaluateMaterialPolicy` workflow is an
// in-process facade QUERY, not an appContract command — so, exactly as
// `evaluateMaterial` reconstructs `MaterialQuality` from the two pure signal
// commands, this reconstructs the `PolicyEvaluation` client-side: parse the same
// linguistics + grounding wire into the core signal shapes, map each to an
// `Observation` via the pure `Core.Policy.observe*` predicates (the single home
// of the thresholds + score formulas — never duplicated here), and fold with
// `Core.Policy.evaluate` against the built-in `qualityPolicy` at the caller's
// boundary. This keeps the wire unchanged. A grounding refusal (no sources)
// degrades its constraint to `pending` — mirroring the workflow — rather
// than an error; a linguistics failure is a genuine error (same as
// `evaluateMaterial`). The judged distractor is always `pending`: no evaluator
// is wired and the core never fabricates a verdict.

const policyGroundingObservations = (
    rGround: Result.Result<unknown, ExternalError>,
): readonly Core.Policy.Observation.Observation[] => {
    if (Result.isErr(rGround)) {
        return [
            Core.Policy.Observation.pending(Core.Policy.citationCoverage.id),
        ];
    }
    return [
        Core.Policy.observeCitationCoverage(
            readSupport(fields(rGround.value)?.get("support")),
        ),
    ];
};

const evaluateMaterialPolicy = async (
    materialId: MaterialId,
    boundary: Core.Policy.Boundary.Boundary,
): PortResult<Core.Policy.Evaluate.PolicyEvaluation> => {
    const axes = await axesOf(materialId);
    if (Result.isErr(axes)) return axes;
    const rLing = axes.value.linguistics;
    if (Result.isErr(rLing)) return rLing;
    const lixF = fields(fields(rLing.value)?.get("lix"));
    const catStr = str(lixF?.get("category"));
    const lix: Core.Quality.Lix.LixResult = {
        score: num(lixF?.get("score")) ?? 0,
        words: num(lixF?.get("words")) ?? 0,
        sentences: num(lixF?.get("sentences")) ?? 0,
        longWords: num(lixF?.get("longWords")) ?? 0,
        category: isBand(catStr) ? catStr : undefined,
    };
    const rGround = axes.value.grounding;
    const observations: readonly Core.Policy.Observation.Observation[] = [
        Core.Policy.observeReadability(lix),
        ...policyGroundingObservations(rGround),
        Core.Policy.Observation.pending(Core.Policy.distractorPlausibility.id),
    ];
    return Result.ok(
        Core.Policy.evaluate(Core.Policy.qualityPolicy, boundary, observations),
    );
};

export const qualityHttp: QualityPorts = {
    evaluateMaterial,
    evaluateMaterials,
    evaluateMaterialPolicy,
};
